import {
    CroppedRegion,
    DetectedItem,
    DetectedItemConfirmationReason,
    DetectedItemSource,
    ImageAnalysisProviderMetadata,
    ImageAnalysisProviderSourceStatus,
    ImageAnalysisIssueCode,
    ImageAnalysisRequest,
    ImageAnalysisResponse,
    ImageAnalysisStatus,
    ImageEmbeddingSearchHint,
    ImageEmbeddingSearchRequest,
    ImageEmbeddingSearchResponse,
    LookitItemCategoryId,
    PolygonPoint,
    SelectedRegionCrop,
    SensitiveRegionActionRequired,
    SensitiveRegionCandidate,
    SensitiveRegionCandidateSource,
    SensitiveRegionDetectionStatus,
    SensitiveRegionKind,
    SensitiveRegionRedactionStatus
} from './domain';
import { defaultRegionForCategory } from './category-default-regions';
import type {
    SensitiveRegionDetectionRequest
} from './sensitive-region-service';
import type {
    ImageEmbeddingCreateResponse
} from './embedding-provider';

export type ImageAnalysisProviderKind = 'mock' | 'internal_api' | 'external_ai';
export type ImageAnalysisProviderId = 'mock' | 'api' | 'external_ai' | (string & {});
export type ImageAnalysisProviderCapability =
    | 'garment_analysis'
    | 'sensitive_region_detection'
    | 'garment_segmentation'
    | 'crop_analysis'
    | 'embedding'
    | 'ocr_hints';

export interface ImageAnalysisProviderGarmentResponse {
    status?: unknown;
    detectedItems?: unknown;
    items?: unknown;
    garments?: unknown;
    suggestedRegion?: unknown;
    suggestedCategoryId?: unknown;
    confidence?: unknown;
    needsUserConfirmation?: unknown;
    issueCode?: unknown;
    message?: unknown;
    sourceStatus?: unknown;
    providerMetadata?: unknown;
}

export interface ImageAnalysisProviderCropAnalysisRequest extends ImageAnalysisRequest {
    selectedRegion: CroppedRegion;
    selectedCategoryId: LookitItemCategoryId | string;
    selectedCrop?: SelectedRegionCrop | null;
}

export interface ImageAnalysisProviderOcrHintsResponse {
    hints: ImageEmbeddingSearchHint[];
    sourceStatus: ImageAnalysisProviderSourceStatus;
    confidence: number | null;
    providerMetadata: ImageAnalysisProviderMetadata;
}

export type ImageAnalysisProviderDetectionResponse = ImageAnalysisResponse & {
    sourceStatus: ImageAnalysisProviderSourceStatus;
    providerMetadata: ImageAnalysisProviderMetadata;
};

export type ImageAnalysisProviderEmbeddingResponse = ImageEmbeddingCreateResponse & {
    sourceStatus: ImageAnalysisProviderSourceStatus;
    providerMetadata: ImageAnalysisProviderMetadata;
};

export type ImageAnalysisProviderEmbeddingCandidatesResponse = Omit<ImageEmbeddingSearchResponse, 'sourceStatus'> & {
    sourceStatus: ImageAnalysisProviderSourceStatus;
    imageEmbeddingSourceStatus: ImageEmbeddingSearchResponse['sourceStatus'];
    confidence: number | null;
    providerMetadata: ImageAnalysisProviderMetadata;
};

export interface GarmentSegmentationRequest {
    request: ImageAnalysisRequest;
    detectedItem: DetectedItem;
}

export interface GarmentSegmentationResponse {
    itemId: string;
    maskUrl?: string | null;
    maskPolygon?: PolygonPoint[] | null;
    croppedRegion?: CroppedRegion | null;
    confidence?: number | null;
}

export interface ImageAnalysisProvider {
    id: ImageAnalysisProviderId;
    label: string;
    kind: ImageAnalysisProviderKind;
    capabilities: readonly ImageAnalysisProviderCapability[];
    requiresExternalTransferGuard?: boolean;
    getMetadata?(
        sourceStatus?: ImageAnalysisProviderSourceStatus,
        confidence?: number | null
    ): ImageAnalysisProviderMetadata;
    detectItems(request: ImageAnalysisRequest): Promise<unknown>;
    requestCropAnalysis?(request: ImageAnalysisProviderCropAnalysisRequest): Promise<unknown>;
    createEmbedding?(request: ImageEmbeddingSearchRequest): Promise<unknown>;
    getEmbeddingCandidates?(request: ImageEmbeddingSearchRequest, limit?: number): Promise<unknown>;
    analyzeOcrHints?(request: ImageAnalysisRequest): Promise<ImageAnalysisProviderOcrHintsResponse>;
    analyzeGarments(request: ImageAnalysisRequest): Promise<unknown>;
    detectSensitiveRegions?(request: SensitiveRegionDetectionRequest): Promise<unknown>;
    segmentGarment?(request: GarmentSegmentationRequest): Promise<GarmentSegmentationResponse>;
}

export interface NormalizeGarmentProviderResponseOptions {
    providerId: ImageAnalysisProviderId;
    defaultSource?: DetectedItemSource;
}

export interface NormalizedSensitiveRegionProviderResponse {
    status: SensitiveRegionDetectionStatus;
    candidates: SensitiveRegionCandidate[];
    needsUserReview: boolean;
    warning: string | null;
}

const imageAnalysisIssueCodes: ImageAnalysisIssueCode[] = [
    'no_item_detected',
    'low_confidence',
    'multiple_items_confused',
    'unsupported_image',
    'non_fashion_image'
];
const detectedItemSources: DetectedItemSource[] = ['mock', 'ai_model', 'heuristic', 'manual', 'user_adjusted', 'system_fallback'];
const confirmationReasons: DetectedItemConfirmationReason[] = [
    'low_confidence',
    'multi_item_overlap',
    'privacy_redaction',
    'unclear_category',
    'low_resolution',
    'unknown'
];
const sensitiveRegionKinds: SensitiveRegionKind[] = [
    'face',
    'account_name',
    'dm_text',
    'location_text',
    'bystander',
    'unknown_text'
];
const sensitiveRegionSources: SensitiveRegionCandidateSource[] = [
    'mock',
    'face_detection',
    'ocr',
    'layout_heuristic',
    'manual',
    'user_report',
    'provider'
];
const sensitiveRegionActions: SensitiveRegionActionRequired[] = ['blur', 'exclude_from_external_transfer', 'review', 'none'];
const sensitiveRegionRedactionStatuses: SensitiveRegionRedactionStatus[] = [
    'pending',
    'redacted',
    'excluded',
    'dismissed',
    'failed',
    'not_required'
];

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isImageAnalysisStatus(value: unknown): value is ImageAnalysisStatus {
    return value === 'detected' || imageAnalysisIssueCodes.includes(value as ImageAnalysisIssueCode);
}

function finiteNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function boundedConfidence(value: unknown, fallback = 0): number {
    const numberValue = finiteNumber(value);
    if (numberValue === null) return fallback;
    return Math.max(0, Math.min(1, numberValue));
}

function optionalConfidence(value: unknown): number | null {
    const numberValue = finiteNumber(value);
    return numberValue === null ? null : Math.max(0, Math.min(1, numberValue));
}

function normalizedProviderSourceStatus(value: unknown, status: ImageAnalysisStatus): ImageAnalysisProviderSourceStatus {
    if (
        value === 'mock' ||
        value === 'image_match_ready' ||
        value === 'api_pending' ||
        value === 'needs_verification' ||
        value === 'provider_timeout' ||
        value === 'privacy_blocked' ||
        value === 'low_confidence' ||
        value === 'no_detection'
    ) {
        return value;
    }

    if (status === 'no_item_detected') return 'no_detection';
    if (status === 'low_confidence' || status === 'multiple_items_confused') return 'low_confidence';
    return 'image_match_ready';
}

function normalizedProviderMetadata(
    value: unknown,
    options: NormalizeGarmentProviderResponseOptions,
    sourceStatus: ImageAnalysisProviderSourceStatus,
    confidence: number | null
): ImageAnalysisProviderMetadata {
    const record = isRecord(value) ? value : {};
    const providerId = stringValue(record.providerId) || stringValue(record.provider_id) || options.providerId;
    const providerKind = stringValue(record.providerKind) || stringValue(record.provider_kind) || (
        providerId === 'mock' || options.defaultSource === 'mock'
            ? 'mock'
            : providerId === 'api'
                ? 'internal_api'
                : 'external_ai'
    );

    return {
        providerId,
        providerLabel: stringValue(record.providerLabel) || stringValue(record.provider_label) || providerId,
        providerKind,
        sourceStatus: normalizedProviderSourceStatus(record.sourceStatus || record.source_status || sourceStatus, sourceStatus === 'no_detection' ? 'no_item_detected' : 'detected'),
        confidence: optionalConfidence(record.confidence) ?? confidence,
        modelVersion: stringValue(record.modelVersion) || stringValue(record.model_version),
        requestId: stringValue(record.requestId) || stringValue(record.request_id),
        externalCall: booleanValue(record.externalCall) ?? false,
        statusDetail: stringValue(record.statusDetail) || stringValue(record.status_detail)
    };
}

function stringValue(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function booleanValue(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
}

function normalizedRegion(value: unknown): CroppedRegion | null {
    if (!isRecord(value)) return null;
    const x = finiteNumber(value.x);
    const y = finiteNumber(value.y);
    const width = finiteNumber(value.width);
    const height = finiteNumber(value.height);
    if (x === null || y === null || width === null || height === null) return null;
    if (width <= 0 || height <= 0) return null;

    return { x, y, width, height };
}

function fallbackRegionForCategory(categoryId: string): CroppedRegion {
    return cloneData(defaultRegionForCategory(categoryId) || defaultRegionForCategory('other')!);
}

function normalizedDetectedItemSource(value: unknown, fallback: DetectedItemSource): DetectedItemSource {
    return detectedItemSources.includes(value as DetectedItemSource) ? value as DetectedItemSource : fallback;
}

function normalizedConfirmationReason(value: unknown, fallback: DetectedItemConfirmationReason | null): DetectedItemConfirmationReason | null {
    return confirmationReasons.includes(value as DetectedItemConfirmationReason)
        ? value as DetectedItemConfirmationReason
        : fallback;
}

function normalizedStringArray(value: unknown): string[] {
    return Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '').map((item) => item.trim())
        : [];
}

function normalizedPolygon(value: unknown): PolygonPoint[] | null {
    if (!Array.isArray(value)) return null;
    const points = value
        .map((point) => {
            if (!isRecord(point)) return null;
            const x = finiteNumber(point.x);
            const y = finiteNumber(point.y);
            return x === null || y === null ? null : { x, y };
        })
        .filter((point): point is PolygonPoint => Boolean(point));

    return points.length > 0 ? points : null;
}

function itemNeedsConfirmation(input: {
    confidence: number;
    categoryConfidence: number | null;
    providerFlag: boolean | null;
    regionProvided: boolean;
}): boolean {
    if (input.providerFlag !== null) return input.providerFlag;
    return Boolean(
        !input.regionProvided ||
        input.confidence < 0.65 ||
        input.categoryConfidence === null ||
        input.categoryConfidence < 0.8
    );
}

function detectedCandidatePriority(item: DetectedItem): number {
    const confirmationPenalty = item.needsUserConfirmation ? 0.15 : 0;
    return item.confidence - confirmationPenalty;
}

function compareDetectedCandidates(a: DetectedItem, b: DetectedItem): number {
    const priorityDiff = detectedCandidatePriority(b) - detectedCandidatePriority(a);
    if (priorityDiff !== 0) return priorityDiff;
    return a.item_index - b.item_index;
}

function bestDetectedCandidate(items: DetectedItem[]): DetectedItem | null {
    return [...items].sort(compareDetectedCandidates)[0] || null;
}

function statusFromDetectedItems(items: DetectedItem[]): ImageAnalysisStatus {
    if (items.length === 0) return 'no_item_detected';
    const ranked = [...items].sort(compareDetectedCandidates);
    const first = ranked[0] || null;
    const second = ranked[1] || null;
    if (!first) return 'no_item_detected';
    if (second && Math.abs(detectedCandidatePriority(first) - detectedCandidatePriority(second)) < 0.04 && first.confidence < 0.75) {
        return 'multiple_items_confused';
    }
    return first.needsUserConfirmation ? 'low_confidence' : 'detected';
}

function issueMessage(status: ImageAnalysisStatus): string | null {
    if (status === 'no_item_detected') return '자동 감지 후보가 없어 사용자가 직접 영역을 선택해야 합니다.';
    if (status === 'low_confidence') return '자동 감지 신뢰도가 낮아 영역과 카테고리 확인이 필요합니다.';
    if (status === 'multiple_items_confused') return '여러 의류 후보가 겹쳐 사용자가 찾을 영역을 직접 확정해야 합니다.';
    if (status === 'unsupported_image') return '지원하지 않는 이미지 형식입니다. jpg, png, webp 이미지로 다시 시도해 주세요.';
    if (status === 'non_fashion_image') return '패션 상품으로 판단할 수 없는 이미지입니다. 옷이 잘 보이는 사진으로 다시 시도해 주세요.';
    return null;
}

function detectedItemsFromProviderPayload(payload: Record<string, unknown>): unknown[] {
    if (Array.isArray(payload.detectedItems)) return payload.detectedItems;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.garments)) return payload.garments;
    return [];
}

export function normalizeDetectedItemCandidate(
    candidate: unknown,
    index: number,
    options: NormalizeGarmentProviderResponseOptions
): DetectedItem | null {
    if (!isRecord(candidate)) return null;

    const category = stringValue(candidate.category)
        || stringValue(candidate.suggestedCategoryId)
        || 'unknown';
    const providedRegion = normalizedRegion(candidate.boundingBox)
        || normalizedRegion(candidate.cropped_region)
        || normalizedRegion(candidate.region);
    const boundingBox = providedRegion || fallbackRegionForCategory(category);
    const confidence = boundedConfidence(candidate.confidence, 0);
    const suggestedCategoryConfidence = optionalConfidence(candidate.suggestedCategoryConfidence);
    const categoryConfidence = optionalConfidence(candidate.categoryConfidence) ?? suggestedCategoryConfidence;
    const needsUserConfirmation = itemNeedsConfirmation({
        confidence,
        categoryConfidence,
        providerFlag: booleanValue(candidate.needsUserConfirmation),
        regionProvided: Boolean(providedRegion)
    });
    const itemIndex = finiteNumber(candidate.item_index);
    const defaultSource = options.defaultSource || 'ai_model';

    return {
        id: stringValue(candidate.id) || `${options.providerId}-detected-${index}`,
        item_index: itemIndex === null ? index : Math.round(itemIndex),
        category,
        label: stringValue(candidate.label) || category,
        confidence,
        suggestedCategoryId: stringValue(candidate.suggestedCategoryId) as LookitItemCategoryId | string | null,
        suggestedCategoryConfidence,
        categoryConfidence,
        boundingBox: cloneData(boundingBox),
        maskUrl: stringValue(candidate.maskUrl),
        maskPolygon: normalizedPolygon(candidate.maskPolygon),
        coverageRatio: optionalConfidence(candidate.coverageRatio),
        fitQuality: candidate.fitQuality as DetectedItem['fitQuality'] || null,
        occlusionLevel: candidate.occlusionLevel as DetectedItem['occlusionLevel'] || null,
        poseKeypointsRef: isRecord(candidate.poseKeypointsRef) ? candidate.poseKeypointsRef as unknown as DetectedItem['poseKeypointsRef'] : null,
        source: normalizedDetectedItemSource(candidate.source, defaultSource),
        needsUserConfirmation,
        confirmationReason: normalizedConfirmationReason(
            candidate.confirmationReason,
            needsUserConfirmation ? 'low_confidence' : null
        ),
        colors: normalizedStringArray(candidate.colors),
        cropped_region: cloneData(normalizedRegion(candidate.cropped_region) || boundingBox)
    };
}

export function normalizeGarmentProviderResponse(
    payload: unknown,
    options: NormalizeGarmentProviderResponseOptions
): ImageAnalysisResponse {
    const record = isRecord(payload) ? payload : {};
    const detectedItems = detectedItemsFromProviderPayload(record)
        .map((item, index) => normalizeDetectedItemCandidate(item, index, options))
        .filter((item): item is DetectedItem => Boolean(item));
    const status = isImageAnalysisStatus(record.status)
        ? record.status
        : statusFromDetectedItems(detectedItems);
    const bestItem = status === 'multiple_items_confused' ? null : bestDetectedCandidate(detectedItems);
    const suggestedRegion = normalizedRegion(record.suggestedRegion)
        || (bestItem ? cloneData(bestItem.cropped_region || bestItem.boundingBox) : null);
    const issueCode = status === 'detected'
        ? (imageAnalysisIssueCodes.includes(record.issueCode as ImageAnalysisIssueCode) ? record.issueCode as ImageAnalysisIssueCode : null)
        : (imageAnalysisIssueCodes.includes(record.issueCode as ImageAnalysisIssueCode) ? record.issueCode as ImageAnalysisIssueCode : status as ImageAnalysisIssueCode);
    const confidence = optionalConfidence(record.confidence) ?? bestItem?.confidence ?? null;
    const sourceStatus = normalizedProviderSourceStatus(record.sourceStatus || record.source_status, status);
    const providerMetadata = normalizedProviderMetadata(record.providerMetadata || record.provider_metadata, options, sourceStatus, confidence);

    return {
        status,
        detectedItems,
        suggestedRegion,
        suggestedCategoryId: stringValue(record.suggestedCategoryId) || bestItem?.suggestedCategoryId || null,
        confidence,
        needsUserConfirmation: booleanValue(record.needsUserConfirmation) ?? (status !== 'detected' || Boolean(bestItem?.needsUserConfirmation)),
        issueCode,
        message: stringValue(record.message) || issueMessage(status),
        sourceStatus,
        providerMetadata
    };
}

function normalizedSensitiveRegionKind(value: unknown): SensitiveRegionKind {
    return sensitiveRegionKinds.includes(value as SensitiveRegionKind) ? value as SensitiveRegionKind : 'unknown_text';
}

function normalizedSensitiveRegionSource(value: unknown): SensitiveRegionCandidateSource {
    return sensitiveRegionSources.includes(value as SensitiveRegionCandidateSource) ? value as SensitiveRegionCandidateSource : 'provider';
}

function normalizedSensitiveRegionAction(value: unknown, kind: SensitiveRegionKind): SensitiveRegionActionRequired {
    if (sensitiveRegionActions.includes(value as SensitiveRegionActionRequired)) return value as SensitiveRegionActionRequired;
    return kind === 'unknown_text' ? 'review' : 'blur';
}

function normalizedRedactionStatus(value: unknown): SensitiveRegionRedactionStatus {
    return sensitiveRegionRedactionStatuses.includes(value as SensitiveRegionRedactionStatus)
        ? value as SensitiveRegionRedactionStatus
        : 'pending';
}

function sensitiveRegionProviderCandidates(payload: Record<string, unknown>): unknown[] {
    if (Array.isArray(payload.candidates)) return payload.candidates;
    if (Array.isArray(payload.sensitiveRegions)) return payload.sensitiveRegions;
    if (Array.isArray(payload.regions)) return payload.regions;
    return [];
}

export function normalizeSensitiveRegionProviderResponse(
    payload: unknown,
    providerId: ImageAnalysisProviderId = 'external_ai'
): NormalizedSensitiveRegionProviderResponse {
    const record = isRecord(payload) ? payload : {};
    const candidates = sensitiveRegionProviderCandidates(record)
        .map((candidate, index): SensitiveRegionCandidate | null => {
            if (!isRecord(candidate)) return null;
            const kind = normalizedSensitiveRegionKind(candidate.kind || candidate.type);
            const boundingBox = normalizedRegion(candidate.boundingBox) || normalizedRegion(candidate.region);
            if (!boundingBox) return null;
            const actionRequired = normalizedSensitiveRegionAction(candidate.actionRequired, kind);

            return {
                id: stringValue(candidate.id) || `${providerId}-sensitive-${index}`,
                kind,
                type: stringValue(candidate.type) as SensitiveRegionCandidate['type'] || kind,
                confidence: boundedConfidence(candidate.confidence, 0),
                boundingBox,
                source: normalizedSensitiveRegionSource(candidate.source),
                actionRequired,
                redactionStatus: normalizedRedactionStatus(candidate.redactionStatus),
                regionRole: 'sensitive_region' as const,
                note: stringValue(candidate.note),
                maskUrl: stringValue(candidate.maskUrl),
                maskPolygon: normalizedPolygon(candidate.maskPolygon)
            };
        })
        .filter((candidate): candidate is SensitiveRegionCandidate => Boolean(candidate));
    const statusValue = record.status;
    const status: SensitiveRegionDetectionStatus = statusValue === 'failed'
        ? 'failed'
        : candidates.length > 0
            ? 'detected'
            : 'not_detected';

    return {
        status,
        candidates,
        needsUserReview: booleanValue(record.needsUserReview) ?? candidates.some((candidate) => candidate.redactionStatus === 'pending'),
        warning: stringValue(record.warning)
    };
}

export class ImageAnalysisProviderRegistry {
    private providers = new Map<string, ImageAnalysisProvider>();

    register(provider: ImageAnalysisProvider): this {
        this.providers.set(provider.id, provider);
        return this;
    }

    get(providerId: ImageAnalysisProviderId | string | null | undefined): ImageAnalysisProvider | null {
        if (!providerId) return null;
        return this.providers.get(providerId) || null;
    }

    resolve(providerId: ImageAnalysisProviderId | string | null | undefined, fallbackId: ImageAnalysisProviderId = 'mock'): ImageAnalysisProvider | null {
        return this.get(providerId) || this.get(fallbackId);
    }

    list(): ImageAnalysisProvider[] {
        return Array.from(this.providers.values());
    }
}
